import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, filter, map, take, withLatestFrom } from 'rxjs/operators';
import { configureTracking } from 'src/app/@utils/gtm-tracking';
import { ImpressionSource } from 'src/app/@utils/gtm-tracking/events';
import { TrackingObject } from 'src/app/@utils/gtm-tracking/model/TrackingObject';

import {
    DeliveryMethodType,
    ParcelShopAddress,
    PickupInStoreAddress
} from '@accelerator/checkout/model/checkout.model';
import { Injectable } from '@angular/core';
import { PlatformService } from '@core/services/platform.service';
import {
    Address,
    BasketCalculatedLineViewModel,
    BasketLine as OrderBasketLine,
    OrderCouponCodeDiscountViewModel
} from '@models/api-orders.models';
import { ProductDetailViewModel, ProductSearchViewModel } from '@models/api-search.models';
import { TrackingSetupViewModel } from '@models/api.models';
import { mapToPageType } from '@utils/gtm-tracking/utils/mapToPageType';
import { UrlHelperService } from '@core/services/url-helper.service';
import { BreakpointMinService } from '@utils/breakpoint-min.service';
import { PageViewDevice } from '@utils/gtm-tracking/events/trackPageViewEvent';
import { deviceTracking } from '@utils/gtm-tracking/model/DeviceTracking';
import { mapToReserveInStore } from '@utils/gtm-tracking/utils/mapToReserveInStore';
import { mapToSendAsGift } from '@utils/gtm-tracking/utils/mapToSendAsGift';
import { BasketLine } from '@core/services/basket/basket.model';
import { mapToMenuLevels } from '@utils/gtm-tracking/utils/mapToMenuLevels';
import { getFilterTypeEventAndValue } from '@utils/gtm-tracking/utils/mapToFilterTypeEvent';
import { Params } from '@angular/router';
import { mapToStore } from '@utils/gtm-tracking/utils/mapToStore';
import { SameDayDeliveryPlace } from '@shared/same-day-delivery/same-day-delivery.model';
import { CookieConsentService } from '@core/services/cookie-consent/cookie-consent.service';
import { UserData } from '@utils/gtm-tracking/model/UserData';
import { CustomerViewModel } from '@models/api-customers.models';
import { ConsentType } from '@core/services/cookie-consent/cookie-consent.config';
import { mapToTrackingUserData } from '@utils/gtm-tracking/utils/mapToTrackingUserData';
import { ReserveInStoreUserInformation } from '@features/reserve-in-store/reserve-in-store';
import { hashObjectValues } from '@utils/hash-object-values';
import { ServerTokensService } from '@core/services/server-tokens.service';
import { configureBotTracking } from '@utils/gtm-tracking/core/tracking';
import { PromotionSettings } from '@utils/gtm-tracking/events/trackPromotionViewEvent';

@Injectable({
    providedIn: 'root'
})
export class GtmService {
    private readonly impressionQueueBs: BehaviorSubject<any> = new BehaviorSubject([]);
    private readonly isGtmReadyS$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    private trackingObject: TrackingObject;
    private userData: UserData;
    private temporaryUserData: UserData | undefined;
    private alreadyTracked: boolean = false;
    private deviceType: PageViewDevice;
    private initialLocation: string = '';
    private trackNextProductClick: boolean = false;

    public isGtmReady$: Observable<boolean> = this.isGtmReadyS$.asObservable();

    constructor(
        private readonly platformService: PlatformService,
        private readonly urlHelperService: UrlHelperService,
        private readonly cookieService: CookieConsentService,
        private readonly serverTokensService: ServerTokensService,
        private readonly breakpointMinService: BreakpointMinService
    ) {
        this.impressionQueueBs
            .pipe(
                filter(impressionQueue => Boolean(impressionQueue.length)),
                debounceTime(1000)
            )
            .subscribe(queue => {
                // get the first item of the list to identify the impression list
                const [first] = queue;
                this.trackingEvents.trackProductImpression(queue, first?.list || 'other');
                this.impressionQueueBs.next([]);
            });

        this.breakpointMinService.breakpoint$
            .pipe(take(1), map(deviceTracking.bind(this)))
            .subscribe(device => (this.deviceType = device));

        // default tracking for the SSR or Bots
        if (this.serverTokensService.isBot || this.platformService.isServer) {
            this.trackingObject = configureBotTracking;
        }
    }

    public initGTM(trackingSetupObject: TrackingSetupViewModel): void {
        const { gtmEnabled = false, gtmId = '' } = trackingSetupObject;

        const windowReference = this.platformService.isBrowser ? window : undefined;

        if (this.serverTokensService.isBot || this.platformService.isServer) {
            return;
        }

        this.trackingObject = configureTracking({
            window: windowReference,
            gtmId,
            trackingEnabled: gtmEnabled,
            debugMode: windowReference?.location.search.includes('gtm_debug=x'),
            globalParamExtractor: this.userDataExtractor.bind(this)
        });

        this.trackingObject.loadComplete.then(response => {
            if (this.platformService.isBrowser) {
                this.isGtmReadyS$.next(true);

                if (response.status === 'ERROR') {
                    this.isGtmReadyS$.next(false);
                    console.error(response.rejectionReason);
                }
            }
        });
    }

    /**
     * Tracking the page view event and where the user starts from
     * @param pageUrl
     * @param title
     * @param contentTypeAlias
     */
    public trackPageViewEvent(pageUrl: string, title?: string, contentTypeAlias?: string): void {
        this.isGtmReady$
            .pipe(take(1), withLatestFrom(this.cookieService.getCookieConsentUUID()))
            .subscribe(([_, guid]) => {
                if (!this.alreadyTracked) {
                    this.initialLocation = pageUrl;
                    this.alreadyTracked = true;
                }

                // let's clear the temporary user data to be sure when we do the track page view event we don't have any data
                this.setTemporaryUserData();

                this.trackingEvents.trackPage({
                    deviceType: this.deviceType,
                    ogLocation: this.initialLocation,
                    pageType: mapToPageType(contentTypeAlias),
                    pageTitle: title ?? '',
                    guid
                });
            });
    }

    public get trackingEvents(): TrackingObject {
        if (!this.trackingObject) {
            console.error(
                'Tracking missing init configuration, please make sure `configureTracking` function has been called'
            );
        }

        return this.trackingObject;
    }

    public trackAddContactInfo(): void {
        this.trackingEvents.trackCustomEvent({
            event: 'add_contact_info'
        });
    }

    /**
     * Track the reserve in store button click
     * @param product
     * @param storeName
     */
    public trackReserveInStoreClick(
        product: Partial<ProductSearchViewModel | ProductDetailViewModel> & { quantity: number },
        storeName: string = ''
    ): void {
        this.trackingEvents.trackCustomEvent({
            event: 'reserve_in_store_click',
            ...mapToReserveInStore(product, storeName)
        });
    }

    /**
     * Track the store reservation when it's completed
     * @param products
     * @param storeName
     */
    public trackReserveInStoreReservation(
        products: Partial<ProductSearchViewModel | ProductDetailViewModel | BasketCalculatedLineViewModel[]>,
        storeName: string = ''
    ): void {
        this.trackingEvents.trackCustomEvent({
            event: 'generate_reservation',
            ...mapToReserveInStore(products, storeName)
        });
    }

    /**
     *  track when the user click on the store link
     *  click event based
     * @param storeName
     */
    public trackGoToStore(storeName: string): void {
        this.trackingEvents.trackCustomEvent({
            event: 'go_to_store',
            store: storeName
        });
    }

    /**
     *  used to track when the user lands on the find store page
     */
    public trackFindStore(): void {
        this.trackingEvents.trackCustomEvent({
            event: 'find_store'
        });
    }

    /**
     * Used to track when tier is changed or updated.
     * @param products
     * @param basketValue
     * @param coupon
     * @param provider
     * @param shippingTier
     * @param tierName
     * @param store
     */
    public trackAddShipping(
        products: OrderBasketLine[],
        basketValue: number = 0,
        coupon: string[] = [],
        provider: string = '',
        shippingTier?: string,
        store?: string | PickupInStoreAddress | ParcelShopAddress
    ): void {
        const includeCompanyName =
            shippingTier === DeliveryMethodType.ParcelShop || shippingTier === DeliveryMethodType.CollectInShop;

        const storeInfo = includeCompanyName ? mapToStore(store) : undefined;

        if (shippingTier) {
            this.trackingEvents.trackAddShippingInfo(products, basketValue, coupon, provider, shippingTier, storeInfo);
        }
    }

    /**
     * used to track when user clicks on the gift card button
     * @param data
     */
    public trackSendAsAGiftClick(data: Partial<ProductDetailViewModel | BasketLine>): void {
        if (!data) {
            return;
        }

        this.trackingEvents.trackCustomEvent({
            event: 'send_as_a_gift_click',
            ...mapToSendAsGift(data)
        });
    }

    /**
     * Used to track when the user added a gift card or wrapping to a product
     * @param data
     */
    public trackSendAsAGiftToBasket(data: Partial<ProductDetailViewModel | BasketLine>): void {
        if (!data) {
            return;
        }

        this.trackingEvents.trackCustomEvent({
            event: 'send_as_a_gift_to_basket',
            ...mapToSendAsGift(data)
        });
    }

    /**
     * Tracking the events on the filters pages but only for some specific filters
     * @param param
     */
    public trackFilterChanges(param: Params): void {
        const { event, property, value } = getFilterTypeEventAndValue(param) || {};
        if (event && property) {
            this.trackingEvents.trackCustomEvent({
                event: event,
                ...{ [property]: value ?? '' }
            });
        }
    }

    public trackStartChat(): void {
        this.trackingEvents.trackCustomEvent({
            event: 'chat_start'
        });
    }

    public trackMenuNavigation(
        url: string,
        productId?: string,
        productName?: string,
        trackProductClick: boolean = false
    ): void {
        // this will track only in case of the previous action was menu navigation and lands on a PLP
        if (this.trackNextProductClick && productId && productName) {
            this.trackingEvents.trackCustomEvent({
                event: 'menu_usage',
                ...mapToMenuLevels(`${this.urlHelperService.getOrigin()}${url}`, productId, productName)
            });
            this.trackNextProductClick = false;
            return;
        }

        // this will track only in case of the user navigate on the menu
        if (url && trackProductClick) {
            this.trackNextProductClick = trackProductClick;
            this.trackingEvents.trackCustomEvent({
                event: 'menu_usage',
                ...mapToMenuLevels(`${this.urlHelperService.getOrigin()}${url}`)
            });
        }
    }

    public trackSeeStoreStockFilter(zipCode: string): void {
        this.trackingEvents.trackCustomEvent({
            event: 'see_store_stock_plp',
            zip_code: zipCode
        });
    }

    /**
     * Let track when the user put a zip code for the same day delivery
     * @param zipCode
     * @param place
     */
    public trackSameDayDelivery(zipCode: string, place: SameDayDeliveryPlace): void {
        this.trackingEvents.trackCustomEvent({
            event: `same_day_zip_code_${place}`,
            zip_code: zipCode
        });
    }

    public trackPromotionView(promotionSetting: PromotionSettings, product?: ProductSearchViewModel): void {
        this.trackingEvents.trackPromotionView(promotionSetting, product);
    }

    /**
     * Push to impression list, will fire after one second where nothing has been added
     * @param product
     * @param list
     */
    public addToImpressionQueue(product: ProductSearchViewModel, list: ImpressionSource): void {
        this.impressionQueueBs
            .pipe(
                filter(() => this.platformService.isBrowser),
                map(impressionQueue => [...impressionQueue, { ...product, list }]),
                take(1)
            )
            .subscribe(queue => {
                this.impressionQueueBs.next(queue);
            });
    }

    /**
     * Return coupon codes as an array of strings
     * @param coupons
     */
    public getCouponCodes(coupons: OrderCouponCodeDiscountViewModel[]): string[] | undefined {
        if (!coupons?.length) {
            return;
        }
        return coupons.map(coupon => coupon.couponCode);
    }

    public userDataExtractor(): Record<string, UserData> {
        const hashedUserData = hashObjectValues<UserData>(this.temporaryUserData ?? this.userData, [
            'customerType',
            'loginStatus'
        ]);
        return { userData: hashedUserData };
    }

    public setUserData(
        data: CustomerViewModel | Address | ReserveInStoreUserInformation,
        consentType: ConsentType = ConsentType.Marketing
    ): void {
        this.cookieService.consentStatus$.pipe(take(1)).subscribe(consentStatus => {
            const hasConsent = consentStatus[consentType];
            this.userData = mapToTrackingUserData(data, hasConsent);
        });
    }

    public setTemporaryUserData(
        data?: CustomerViewModel | Address | ReserveInStoreUserInformation,
        consentType: ConsentType = ConsentType.Marketing
    ): void {
        this.cookieService.consentStatus$.pipe(take(1)).subscribe(consentStatus => {
            const hasConsent = consentStatus[consentType];

            this.temporaryUserData = undefined;

            if (data) {
                this.temporaryUserData = mapToTrackingUserData(data, hasConsent);
            }
        });
    }
}
